import React, { createContext, useContext, useEffect, useState, useRef } from 'react';
import confetti from 'canvas-confetti';
import {
  Account,
  Budget,
  Category,
  Currency,
  DailyReportEntry,
  Debt,
  FinanceFolder,
  FinanceFolderType,
  Habit,
  HabitLog,
  Language,
  Note,
  RecurringBill,
  ResourceLink,
  SavingsGoal,
  Task,
  Theme,
  Transaction,
  User,
  YearlyGoal,
  GoalStatus,
  GoalCategory
} from '../types';
import { translations } from '../lib/i18n';
import { StorageService, DEFAULT_FINANCE_FOLDERS, DEFAULT_YEARLY_GOALS, DEFAULT_RESOURCES } from '../lib/storage';
import { CloudSync, CloudSyncState } from '../lib/cloudSync';
import { getLocalDateString } from '../lib/dateUtils';
import {
  isBiometricsAvailable,
  registerBiometrics,
  authenticateBiometrics,
  hashPin,
  verifyPin,
  hashString,
  verifyPassword,
} from '../lib/biometrics';


export type PageView =
  | 'landing'
  | 'login'
  | 'signup'
  | 'dashboard'
  | 'finance'
  | 'notes'
  | 'tasks'
  | 'goals'
  | 'resources'
  | 'habits'
  | 'calendar'
  | 'reports'
  | 'settings';

interface AppContextType {
  // State
  user: User | null;
  isAuthenticated: boolean;
  currentPage: PageView;
  language: Language;
  theme: Theme;
  currency: Currency;
  t: typeof translations['en'];
  
  // App Lock & Biometrics Security
  isAppLockEnabled: boolean;
  isBiometricsEnabled: boolean;
  isBiometricsAvailable: boolean;
  isAppLocked: boolean;
  autoLockTimeout: number;
  lockApp: () => void;
  unlockWithPin: (pin: string) => Promise<boolean>;
  unlockWithBiometrics: () => Promise<boolean>;
  enableAppLock: (pin: string, enableBio?: boolean) => Promise<{ success: boolean; error?: string }>;
  disableAppLock: (pin: string) => Promise<{ success: boolean; error?: string }>;
  changeAppLockPin: (oldPin: string, newPin: string) => Promise<{ success: boolean; error?: string }>;
  toggleBiometrics: (enable: boolean) => Promise<{ success: boolean; error?: string }>;
  setAutoLockTimeout: (timeoutMs: number) => void;
  
  financeFolders: FinanceFolder[];
  selectedFinanceFolderId: string;
  setSelectedFinanceFolderId: (id: string) => void;
  accounts: Account[];
  categories: Category[];
  transactions: Transaction[];
  budgets: Budget[];
  debts: Debt[];
  savingsGoals: SavingsGoal[];
  recurringBills: RecurringBill[];
  yearlyGoals: YearlyGoal[];
  notes: Note[];
  tasks: Task[];
  habits: Habit[];
  habitLogs: HabitLog[];
  personalReports: DailyReportEntry[];

  // Navigation & Modals
  setCurrentPage: (page: PageView) => void;
  isSearchOpen: boolean;
  setIsSearchOpen: (open: boolean) => void;
  activeModal: 'transaction' | 'note' | 'task' | 'habit' | 'debt' | null;
  setActiveModal: (modal: 'transaction' | 'note' | 'task' | 'habit' | 'debt' | null) => void;

  // Preferences & Auth
  setLanguage: (lang: Language) => void;
  setTheme: (theme: Theme) => void;
  setCurrency: (currency: Currency) => void;
  login: (email?: string, password?: string) => Promise<boolean>;
  signup: (name: string, email: string, currency?: Currency) => boolean;
  logout: () => void;
  changePassword: (oldPassword: string, newPassword: string) => Promise<{ success: boolean; error?: string }>;

  updateUser: (data: Partial<User>) => void;
  triggerConfetti: () => void;

  // Finance Folders Actions
  addFinanceFolder: (folder: Omit<FinanceFolder, 'id' | 'createdAt'>) => FinanceFolder;
  updateFinanceFolder: (id: string, data: Partial<FinanceFolder>) => void;
  deleteFinanceFolder: (id: string) => void;

  // Finance Actions
  addTransaction: (tx: Omit<Transaction, 'id' | 'createdAt'>) => void;
  editTransaction: (id: string, tx: Partial<Transaction>) => void;
  deleteTransaction: (id: string) => void;
  transferMoney: (fromAccountId: string, toAccountId: string, amount: number, note?: string) => boolean;
  addCategory: (cat: Omit<Category, 'id'>) => void;
  deleteCategory: (id: string) => void;
  addAccount: (acc: Omit<Account, 'id'>) => void;
  editAccount: (id: string, updated: Partial<Account>) => void;
  updateAccountBalance: (id: string, balance: number) => void;
  deleteAccount: (id: string) => void;
  addBudget: (budget: Omit<Budget, 'id'>) => void;
  editBudget: (id: string, updated: Partial<Budget>) => void;
  deleteBudget: (id: string) => void;
  addDebt: (debt: Omit<Debt, 'id' | 'createdAt' | 'status'>) => void;
  editDebt: (id: string, updated: Partial<Debt>) => void;
  settleDebt: (id: string) => void;
  deleteDebt: (id: string) => void;
  addSavingsGoal: (goal: Omit<SavingsGoal, 'id'>) => void;
  editSavingsGoal: (id: string, updated: Partial<SavingsGoal>) => void;
  updateSavingsGoalProgress: (id: string, deltaAmount: number) => void;
  deleteSavingsGoal: (id: string) => void;
  addRecurringBill: (bill: Omit<RecurringBill, 'id'>) => void;
  editRecurringBill: (id: string, updated: Partial<RecurringBill>) => void;
  deleteRecurringBill: (id: string) => void;
  payRecurringBill: (id: string, accountId: string) => void;

  // Notes Actions
  addNote: (note: Omit<Note, 'id' | 'createdAt' | 'updatedAt'>) => void;
  updateNote: (id: string, note: Partial<Note>) => void;
  deleteNote: (id: string) => void;
  restoreNote: (id: string) => void;
  permanentlyDeleteNote: (id: string) => void;
  emptyTrash: () => void;
  togglePinNote: (id: string) => void;
  duplicateNote: (id: string) => void;
  toggleNoteChecklist: (noteId: string, itemId: string) => void;

  // Tasks Actions
  addTask: (task: Omit<Task, 'id' | 'createdAt' | 'status'>) => void;
  toggleTask: (id: string) => void;
  editTask: (id: string, updated: Partial<Task>) => void;
  deleteTask: (id: string) => void;

  // Habits Actions
  addHabit: (habit: Omit<Habit, 'id' | 'createdAt'>) => void;
  editHabit: (id: string, updated: Partial<Habit>) => void;
  toggleHabit: (habitId: string, date?: string) => void;
  deleteHabit: (id: string) => void;

  // Yearly Goals & Learning Roadmap
  addYearlyGoal: (goal: Omit<YearlyGoal, 'id' | 'createdAt'>) => void;
  editYearlyGoal: (id: string, updated: Partial<YearlyGoal>) => void;
  deleteYearlyGoal: (id: string) => void;
  toggleGoalMilestone: (goalId: string, milestoneId: string) => void;
  updateGoalProgress: (goalId: string, progress: number) => void;

  // Resources & Links Actions
  resources: ResourceLink[];
  addResource: (res: Omit<ResourceLink, 'id' | 'createdAt'>) => void;
  editResource: (id: string, updated: Partial<ResourceLink>) => void;
  deleteResource: (id: string) => void;
  toggleFavoriteResource: (id: string) => void;
  togglePinResource: (id: string) => void;
  recordResourceVisit: (id: string) => void;
  importResources: (links: ResourceLink[]) => void;

  // Personal Report Actions
  updateDailyReport: (entry: DailyReportEntry) => void;
  bulkUpdateDailyReports: (entries: DailyReportEntry[]) => void;
  clearMonthlyReports: (monthStr: string) => void;

  // Data & Reset
  resetData: () => void;
  importBackup: (jsonStr: string) => boolean;

  // Real-Time Cloud Sync
  syncState: CloudSyncState;
  syncNow: () => Promise<boolean>;
  pullNow: (force?: boolean) => Promise<boolean>;
  setSyncKey: (key: string) => void;
  setSyncEndpoint: (endpoint: string) => void;
  setAutoSync: (enabled: boolean) => void;
}

const AppContext = createContext<AppContextType | undefined>(undefined);

export const AppProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const initialData = StorageService.loadAll();

  const [isAuthenticated, setIsAuthenticated] = useState<boolean>(() => {
    const session = localStorage.getItem('mylife_auth_session');
    return session === 'true';
  });

  const [user, setUser] = useState<User | null>(() => {
    const savedUser = StorageService.getItem<User | null>('mylife_user', null);
    return savedUser || initialData.user;
  });

  const [currentPage, setCurrentPage] = useState<PageView>(() => {
    const session = localStorage.getItem('mylife_auth_session');
    return session === 'true' ? 'dashboard' : 'login';
  });

  const [language, setLanguageState] = useState<Language>('en');
  const [theme, setThemeState] = useState<Theme>(() => {
    const isCustomized = localStorage.getItem('mylife_theme_customized') === 'true';
    if (isCustomized) {
      const saved = localStorage.getItem('mylife_theme') as Theme;
      if (saved && ['dark', 'light', 'system'].includes(saved)) {
        return saved;
      }
    }
    return 'system';
  });
  const [currency, setCurrencyState] = useState<Currency>(initialData.user.currency || 'BDT');

  const [financeFolders, setFinanceFolders] = useState<FinanceFolder[]>(() => {
    return initialData.financeFolders && initialData.financeFolders.length > 0
      ? initialData.financeFolders
      : DEFAULT_FINANCE_FOLDERS;
  });
  const [selectedFinanceFolderId, setSelectedFinanceFolderId] = useState<string>('all');

  const [accounts, setAccounts] = useState<Account[]>(initialData.accounts);
  const [categories, setCategories] = useState<Category[]>(initialData.categories);
  const [transactions, setTransactions] = useState<Transaction[]>(initialData.transactions);
  const [budgets, setBudgets] = useState<Budget[]>(initialData.budgets);
  const [debts, setDebts] = useState<Debt[]>(initialData.debts);
  const [savingsGoals, setSavingsGoals] = useState<SavingsGoal[]>(initialData.savingsGoals || []);
  const [recurringBills, setRecurringBills] = useState<RecurringBill[]>(initialData.recurringBills || []);
  const [notes, setNotes] = useState<Note[]>(initialData.notes);
  const [tasks, setTasks] = useState<Task[]>(initialData.tasks);
  const [habits, setHabits] = useState<Habit[]>(initialData.habits);
  const [habitLogs, setHabitLogs] = useState<HabitLog[]>(initialData.habitLogs);
  const [personalReports, setPersonalReports] = useState<DailyReportEntry[]>(initialData.personalReports);
  const [yearlyGoals, setYearlyGoals] = useState<YearlyGoal[]>(initialData.yearlyGoals || []);
  const [resources, setResources] = useState<ResourceLink[]>(initialData.resources || []);

  const [isSearchOpen, setIsSearchOpen] = useState(false);
  const [activeModal, setActiveModal] = useState<'transaction' | 'note' | 'task' | 'habit' | 'debt' | null>(null);

  // App Lock & Biometrics States
  const [isAppLockEnabled, setIsAppLockEnabled] = useState<boolean>(() => {
    return StorageService.getItem<boolean>('mylife_app_lock_enabled', false);
  });
  const [isBiometricsEnabled, setIsBiometricsEnabled] = useState<boolean>(() => {
    return StorageService.getItem<boolean>('mylife_biometrics_enabled', false);
  });
  const [isBiometricsAvailableState, setIsBiometricsAvailableState] = useState<boolean>(false);
  const [isAppLocked, setIsAppLocked] = useState<boolean>(() => {
    const enabled = StorageService.getItem<boolean>('mylife_app_lock_enabled', false);
    const session = localStorage.getItem('mylife_auth_session');
    if (!enabled || session !== 'true') return false;

    // If already unlocked in this active tab session, do not re-lock on page reload
    const isUnlockedInSession = sessionStorage.getItem('mylife_app_unlocked_session') === 'true';
    if (isUnlockedInSession) {
      return false;
    }

    // Fresh app launch after app exit requires unlock
    return true;
  });
  const [autoLockTimeout, setAutoLockTimeoutState] = useState<number>(() => {
    return StorageService.getItem<number>('mylife_autolock_timeout', 0); // 0 = immediately
  });

  const lastHiddenTimeRef = useRef<number | null>(null);

  // Check hardware biometric capability on mount
  useEffect(() => {
    isBiometricsAvailable().then((avail) => {
      setIsBiometricsAvailableState(avail);
    });
  }, []);

  // Auto-lock when user leaves or hides the application (exiting the app)
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (!isAppLockEnabled || !isAuthenticated) return;

      if (document.visibilityState === 'hidden') {
        lastHiddenTimeRef.current = Date.now();
      } else if (document.visibilityState === 'visible') {
        if (lastHiddenTimeRef.current !== null) {
          const elapsed = Date.now() - lastHiddenTimeRef.current;
          // For immediately (0), require a slight 600ms buffer to prevent false-positive reload flashes
          const effectiveTimeout = autoLockTimeout === 0 ? 600 : autoLockTimeout;
          if (elapsed >= effectiveTimeout) {
            sessionStorage.removeItem('mylife_app_unlocked_session');
            setIsAppLocked(true);
          }
          lastHiddenTimeRef.current = null;
        }
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [isAppLockEnabled, isAuthenticated, autoLockTimeout]);

  const lockApp = () => {
    if (isAppLockEnabled) {
      sessionStorage.removeItem('mylife_app_unlocked_session');
      setIsAppLocked(true);
    }
  };

  const unlockWithPin = async (pin: string): Promise<boolean> => {
    const storedHash = StorageService.getItem<string>('mylife_app_lock_pin_hash', '');
    if (!storedHash) return false;
    const isValid = await verifyPin(pin, storedHash);
    if (isValid) {
      sessionStorage.setItem('mylife_app_unlocked_session', 'true');
      setIsAppLocked(false);
      return true;
    }
    return false;
  };

  const unlockWithBiometrics = async (): Promise<boolean> => {
    if (!isBiometricsEnabled) return false;
    const credentialId = StorageService.getItem<string | null>('mylife_biometric_cred_id', null);
    const res = await authenticateBiometrics(credentialId);
    if (res.success) {
      sessionStorage.setItem('mylife_app_unlocked_session', 'true');
      setIsAppLocked(false);
      return true;
    }
    return false;
  };

  const enableAppLock = async (pin: string, enableBio = false): Promise<{ success: boolean; error?: string }> => {
    if (!pin || pin.length < 4) {
      return { success: false, error: 'PIN must be at least 4 digits.' };
    }
    const hash = await hashPin(pin);
    StorageService.setItem('mylife_app_lock_pin_hash', hash);
    StorageService.setItem('mylife_app_lock_enabled', true);
    setIsAppLockEnabled(true);
    sessionStorage.setItem('mylife_app_unlocked_session', 'true');
    setIsAppLocked(false);

    if (enableBio) {
      const bioRes = await registerBiometrics(user?.email || 'user');
      if (bioRes.success) {
        StorageService.setItem('mylife_biometrics_enabled', true);
        if (bioRes.credentialId) {
          StorageService.setItem('mylife_biometric_cred_id', bioRes.credentialId);
        }
        setIsBiometricsEnabled(true);
      }
    }
    return { success: true };
  };

  const disableAppLock = async (pin: string): Promise<{ success: boolean; error?: string }> => {
    const storedHash = StorageService.getItem<string>('mylife_app_lock_pin_hash', '');
    const isValid = await verifyPin(pin, storedHash);
    if (!isValid) {
      return { success: false, error: 'Incorrect PIN.' };
    }
    StorageService.removeItem('mylife_app_lock_enabled');
    StorageService.removeItem('mylife_app_lock_pin_hash');
    StorageService.removeItem('mylife_biometrics_enabled');
    StorageService.removeItem('mylife_biometric_cred_id');
    sessionStorage.removeItem('mylife_app_unlocked_session');
    setIsAppLockEnabled(false);
    setIsBiometricsEnabled(false);
    setIsAppLocked(false);
    return { success: true };
  };

  const changeAppLockPin = async (oldPin: string, newPin: string): Promise<{ success: boolean; error?: string }> => {
    const storedHash = StorageService.getItem<string>('mylife_app_lock_pin_hash', '');
    const isValid = await verifyPin(oldPin, storedHash);
    if (!isValid) {
      return { success: false, error: 'Current PIN is incorrect.' };
    }
    if (!newPin || newPin.length < 4) {
      return { success: false, error: 'New PIN must be at least 4 digits.' };
    }
    const newHash = await hashPin(newPin);
    StorageService.setItem('mylife_app_lock_pin_hash', newHash);
    return { success: true };
  };

  const toggleBiometrics = async (enable: boolean): Promise<{ success: boolean; error?: string }> => {
    if (!enable) {
      StorageService.setItem('mylife_biometrics_enabled', false);
      setIsBiometricsEnabled(false);
      return { success: true };
    }
    const regRes = await registerBiometrics(user?.email || 'user');
    if (regRes.success) {
      StorageService.setItem('mylife_biometrics_enabled', true);
      if (regRes.credentialId) {
        StorageService.setItem('mylife_biometric_cred_id', regRes.credentialId);
      }
      setIsBiometricsEnabled(true);
      return { success: true };
    }
    return { success: false, error: regRes.error || 'Failed to enable biometrics' };
  };

  const setAutoLockTimeout = (timeoutMs: number) => {
    StorageService.setItem('mylife_autolock_timeout', timeoutMs);
    setAutoLockTimeoutState(timeoutMs);
  };

  // Real-Time Cloud Sync State
  const [syncState, setSyncState] = useState<CloudSyncState>(() => CloudSync.getState());

  const isInitializedRef = useRef(false);
  const isRemoteApplyingRef = useRef(false);

  const triggerSafeAutoPush = () => {
    if (!isInitializedRef.current || isRemoteApplyingRef.current) return;
    CloudSync.triggerAutoPush();
  };

  useEffect(() => {
    // Initialize Cloud Sync with live remote update receiver
    CloudSync.init((remoteData) => {
      isRemoteApplyingRef.current = true;
      if (remoteData.accounts !== undefined) setAccounts(remoteData.accounts);
      if (remoteData.categories !== undefined) setCategories(remoteData.categories);
      if (remoteData.transactions !== undefined) setTransactions(remoteData.transactions);
      if (remoteData.budgets !== undefined) setBudgets(remoteData.budgets);
      if (remoteData.debts !== undefined) setDebts(remoteData.debts);
      if (remoteData.savingsGoals !== undefined) setSavingsGoals(remoteData.savingsGoals);
      if (remoteData.recurringBills !== undefined) setRecurringBills(remoteData.recurringBills);
      if (remoteData.notes !== undefined) setNotes(remoteData.notes);
      if (remoteData.tasks !== undefined) setTasks(remoteData.tasks);
      if (remoteData.habits !== undefined) setHabits(remoteData.habits);
      if (remoteData.habitLogs !== undefined) setHabitLogs(remoteData.habitLogs);
      if (remoteData.personalReports !== undefined) setPersonalReports(remoteData.personalReports);
      if (remoteData.yearlyGoals !== undefined) setYearlyGoals(remoteData.yearlyGoals);
      if (remoteData.resources !== undefined) setResources(remoteData.resources);
      if (remoteData.financeFolders !== undefined) setFinanceFolders(remoteData.financeFolders);
      setTimeout(() => {
        isRemoteApplyingRef.current = false;
        isInitializedRef.current = true;
      }, 500);
    });

    const timer = setTimeout(() => {
      isInitializedRef.current = true;
    }, 1500);

    const unsub = CloudSync.subscribe(setSyncState);
    return () => {
      clearTimeout(timer);
      unsub();
    };
  }, []);

  // Sync to localStorage & trigger Cloud Auto-Push
  useEffect(() => {
    StorageService.setItem('mylife_finance_folders', financeFolders);
    triggerSafeAutoPush();
  }, [financeFolders]);

  useEffect(() => {
    StorageService.setItem('mylife_accounts', accounts);
    triggerSafeAutoPush();
  }, [accounts]);

  useEffect(() => {
    StorageService.setItem('mylife_categories', categories);
    triggerSafeAutoPush();
  }, [categories]);

  useEffect(() => {
    StorageService.setItem('mylife_transactions', transactions);
    triggerSafeAutoPush();
  }, [transactions]);

  useEffect(() => {
    StorageService.setItem('mylife_budgets', budgets);
    triggerSafeAutoPush();
  }, [budgets]);

  useEffect(() => {
    StorageService.setItem('mylife_debts', debts);
    triggerSafeAutoPush();
  }, [debts]);

  useEffect(() => {
    StorageService.setItem('mylife_savings_goals', savingsGoals);
    triggerSafeAutoPush();
  }, [savingsGoals]);

  useEffect(() => {
    StorageService.setItem('mylife_recurring_bills', recurringBills);
    triggerSafeAutoPush();
  }, [recurringBills]);

  useEffect(() => {
    StorageService.setItem('mylife_notes', notes);
    triggerSafeAutoPush();
  }, [notes]);

  useEffect(() => {
    StorageService.setItem('mylife_tasks', tasks);
    triggerSafeAutoPush();
  }, [tasks]);

  useEffect(() => {
    StorageService.setItem('mylife_habits', habits);
    triggerSafeAutoPush();
  }, [habits]);

  useEffect(() => {
    StorageService.setItem('mylife_habit_logs', habitLogs);
    triggerSafeAutoPush();
  }, [habitLogs]);

  useEffect(() => {
    StorageService.setItem('mylife_personal_reports', personalReports);
    triggerSafeAutoPush();
  }, [personalReports]);

  useEffect(() => {
    StorageService.setItem('mylife_yearly_goals', yearlyGoals);
    triggerSafeAutoPush();
  }, [yearlyGoals]);

  useEffect(() => {
    StorageService.setItem('mylife_resources', resources);
    triggerSafeAutoPush();
  }, [resources]);

  // Theme application
  const applyTheme = (th: Theme) => {
    const isDark =
      th === 'dark' ||
      (th === 'system' && typeof window !== 'undefined' && window.matchMedia('(prefers-color-scheme: dark)').matches);

    if (isDark) {
      document.documentElement.classList.add('dark');
      document.documentElement.classList.remove('light');
    } else {
      document.documentElement.classList.add('light');
      document.documentElement.classList.remove('dark');
    }
  };

  useEffect(() => {
    applyTheme(theme);
    localStorage.setItem('mylife_theme', theme);

    // If theme is 'system', listen for real-time device OS dark/light mode changes
    if (theme === 'system' && typeof window !== 'undefined' && window.matchMedia) {
      const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
      const handleMediaChange = () => {
        applyTheme('system');
      };

      if (mediaQuery.addEventListener) {
        mediaQuery.addEventListener('change', handleMediaChange);
        return () => mediaQuery.removeEventListener('change', handleMediaChange);
      } else if ((mediaQuery as any).addListener) {
        (mediaQuery as any).addListener(handleMediaChange);
        return () => (mediaQuery as any).removeListener(handleMediaChange);
      }
    }
  }, [theme]);

  // Language translation selector (English)
  const t = translations.en;

  const setLanguage = (_lang?: any) => {
    setLanguageState('en');
    localStorage.setItem('mylife_language', 'en');
  };

  const setTheme = (th: Theme) => {
    if (th === 'system') {
      localStorage.removeItem('mylife_theme_customized');
    } else {
      localStorage.setItem('mylife_theme_customized', 'true');
    }
    setThemeState(th);
    applyTheme(th);
    if (user) {
      const updated = { ...user, theme: th };
      setUser(updated);
      StorageService.setItem('mylife_user', updated);
    }
  };

  const setCurrency = (curr: Currency) => {
    setCurrencyState(curr);
    if (user) {
      const updated = { ...user, currency: curr };
      setUser(updated);
      StorageService.setItem('mylife_user', updated);
    }
  };

  const updateUser = (data: Partial<User>) => {
    if (!user) return;
    const updated = { ...user, ...data };
    setUser(updated);
    StorageService.setItem('mylife_user', updated);
  };

  const triggerConfetti = () => {
    confetti({
      particleCount: 80,
      spread: 70,
      origin: { y: 0.6 },
      colors: ['#10b981', '#3b82f6', '#8b5cf6', '#f59e0b', '#ec4899'],
    });
  };

  const login = async (email?: string, password?: string): Promise<boolean> => {
    const inputEmail = (email || '').trim().toLowerCase();
    const inputPassword = (password || '').trim();

    const storedHash = localStorage.getItem('mylife_user_password_hash');
    const legacyMasterPassword = localStorage.getItem('mylife_user_password') || '987654321';
    const perEmailPassword = localStorage.getItem(`mylife_pwd_${inputEmail}`);

    let isValid = false;

    // 1. Verify against SHA-256 hash if present
    if (storedHash) {
      isValid = await verifyPassword(inputPassword, storedHash);
    }

    // 2. Fallback to legacy plaintext password during migration
    if (!isValid && (inputPassword === legacyMasterPassword || (perEmailPassword && inputPassword === perEmailPassword))) {
      isValid = true;
      // Automatically migrate to secure SHA-256 hash and delete plaintext
      try {
        const newHash = await hashString(inputPassword);
        localStorage.setItem('mylife_user_password_hash', newHash);
        localStorage.removeItem('mylife_user_password');
        if (inputEmail) {
          localStorage.removeItem(`mylife_pwd_${inputEmail}`);
        }
      } catch (err) {
        console.warn('Password migration error:', err);
      }
    }

    if (!isValid) {
      return false;
    }

    const existingUser = StorageService.getItem<User | null>('mylife_user', null);
    let loggedUser: User;
    if (existingUser && existingUser.email.toLowerCase() === inputEmail) {
      loggedUser = existingUser;
    } else {
      loggedUser = {
        id: 'user_' + (inputEmail.split('@')[0] || 'tahsin'),
        name: existingUser?.name || 'Tahsin Miftah',
        email: inputEmail || 'tahsinmiftah@proton.me',
        currency: currency || 'BDT',
        language,
        theme,
      };
    }

    setUser(loggedUser);
    StorageService.setItem('mylife_user', loggedUser);
    const userSyncKey = 'user_' + (inputEmail.split('@')[0] || 'tahsin').replace(/[^a-zA-Z0-9]/g, '_');
    CloudSync.setSyncKey(userSyncKey);
    setIsAuthenticated(true);
    setIsAppLocked(false);
    sessionStorage.setItem('mylife_app_unlocked_session', 'true');
    localStorage.setItem('mylife_auth_session', 'true');
    setCurrentPage('dashboard');
    triggerConfetti();
    return true;
  };

  const signup = (name: string, email: string, curr: Currency = 'BDT') => {
    const inputEmail = email.trim().toLowerCase();
    const newUser: User = {
      id: 'user_' + Date.now(),
      name,
      email: inputEmail,
      currency: curr,
      language,
      theme,
    };
    setUser(newUser);
    StorageService.setItem('mylife_user', newUser);
    const userSyncKey = 'user_' + (inputEmail.split('@')[0] || 'tahsin').replace(/[^a-zA-Z0-9]/g, '_');
    CloudSync.setSyncKey(userSyncKey);
    setIsAuthenticated(true);
    setIsAppLocked(false);
    sessionStorage.setItem('mylife_app_unlocked_session', 'true');
    localStorage.setItem('mylife_auth_session', 'true');
    setCurrentPage('dashboard');
    triggerConfetti();
    return true;
  };

  const logout = () => {
    setIsAuthenticated(false);
    setIsAppLocked(false);
    sessionStorage.removeItem('mylife_app_unlocked_session');
    localStorage.setItem('mylife_auth_session', 'false');
    setCurrentPage('login');
  };

  const changePassword = async (oldPassword: string, newPassword: string): Promise<{ success: boolean; error?: string }> => {
    const storedHash = localStorage.getItem('mylife_user_password_hash');
    const legacyMasterPassword = localStorage.getItem('mylife_user_password') || '987654321';

    let isOldValid = false;
    if (storedHash) {
      isOldValid = await verifyPassword(oldPassword, storedHash);
    } else if (oldPassword === legacyMasterPassword) {
      isOldValid = true;
    }

    if (!isOldValid) {
      return {
        success: false,
        error: 'Current password is incorrect!',
      };
    }
    if (!newPassword || newPassword.length < 4) {
      return {
        success: false,
        error: 'New password must be at least 4 characters long!',
      };
    }

    // Hash the new password and remove plaintext storage
    const newHash = await hashString(newPassword);
    localStorage.setItem('mylife_user_password_hash', newHash);
    localStorage.removeItem('mylife_user_password');
    if (user?.email) {
      localStorage.removeItem(`mylife_pwd_${user.email.toLowerCase()}`);
    }

    triggerConfetti();
    return { success: true };
  };

  // --- Finance Folders ---
  const addFinanceFolder = (folder: Omit<FinanceFolder, 'id' | 'createdAt'>): FinanceFolder => {
    const newFolder: FinanceFolder = {
      ...folder,
      id: 'f_' + Date.now(),
      createdAt: new Date().toISOString(),
    };
    setFinanceFolders(prev => [...prev, newFolder]);
    setSelectedFinanceFolderId(newFolder.id);
    triggerConfetti();
    return newFolder;
  };

  const updateFinanceFolder = (id: string, data: Partial<FinanceFolder>) => {
    setFinanceFolders(prev =>
      prev.map(f => (f.id === id ? { ...f, ...data } : f))
    );
  };

  const deleteFinanceFolder = (id: string) => {
    // If attempting to delete default or only folder, reject
    if (id === 'f_personal' || financeFolders.length <= 1) return;

    // Reassign associated items to 'f_personal'
    setTransactions(prev => prev.map(t => (t.folderId === id ? { ...t, folderId: 'f_personal' } : t)));
    setAccounts(prev => prev.map(a => (a.folderId === id ? { ...a, folderId: 'f_personal' } : a)));
    setDebts(prev => prev.map(d => (d.folderId === id ? { ...d, folderId: 'f_personal' } : d)));
    setBudgets(prev => prev.map(b => (b.folderId === id ? { ...b, folderId: 'f_personal' } : b)));
    setSavingsGoals(prev => prev.map(s => (s.folderId === id ? { ...s, folderId: 'f_personal' } : s)));
    setRecurringBills(prev => prev.map(r => (r.folderId === id ? { ...r, folderId: 'f_personal' } : r)));

    setFinanceFolders(prev => prev.filter(f => f.id !== id));
    if (selectedFinanceFolderId === id) {
      setSelectedFinanceFolderId('all');
    }
  };

  // --- Transactions ---
  const addTransaction = (tx: Omit<Transaction, 'id' | 'createdAt'>) => {
    const newId = 'tx_' + Date.now();
    const cat = categories.find(c => c.id === tx.categoryId);
    const acc = accounts.find(a => a.id === tx.accountId);
    const folderId = tx.folderId || acc?.folderId || (selectedFinanceFolderId !== 'all' ? selectedFinanceFolderId : 'f_personal');

    const newTx: Transaction = {
      ...tx,
      id: newId,
      folderId,
      categoryName: cat?.name || 'Uncategorized',
      accountName: acc?.name || 'Default Account',
      createdAt: new Date().toISOString(),
    };

    setTransactions(prev => [newTx, ...prev]);

    // Update account balance
    const delta = tx.type === 'income' ? tx.amount : -tx.amount;
    setAccounts(prev =>
      prev.map(a => (a.id === tx.accountId ? { ...a, balance: a.balance + delta } : a))
    );
  };

  const editTransaction = (id: string, updated: Partial<Transaction>) => {
    setTransactions(prev =>
      prev.map(t => (t.id === id ? { ...t, ...updated } : t))
    );
  };

  const deleteTransaction = (id: string) => {
    const tx = transactions.find(t => t.id === id);
    if (tx) {
      const delta = tx.type === 'income' ? -tx.amount : tx.amount;
      setAccounts(prev =>
        prev.map(a => (a.id === tx.accountId ? { ...a, balance: a.balance + delta } : a))
      );
      setTransactions(prev => prev.filter(t => t.id !== id));
    }
  };

  const transferMoney = (fromAccountId: string, toAccountId: string, amount: number, note?: string): boolean => {
    const fromAcc = accounts.find(a => a.id === fromAccountId);
    const toAcc = accounts.find(a => a.id === toAccountId);
    if (!fromAcc || !toAcc || amount <= 0) return false;

    const newTx: Transaction = {
      id: 'tx_tr_' + Date.now(),
      type: 'transfer',
      amount,
      accountId: fromAccountId,
      accountName: `${fromAcc.name} → ${toAcc.name}`,
      categoryName: 'Transfer (স্থানান্তর)',
      folderId: fromAcc.folderId || (selectedFinanceFolderId !== 'all' ? selectedFinanceFolderId : 'f_personal'),
      date: getLocalDateString(),
      note: note || `Transferred from ${fromAcc.name} to ${toAcc.name}`,
      paymentMethod: fromAcc.type,
      createdAt: new Date().toISOString(),
    };

    setTransactions(prev => [newTx, ...prev]);
    setAccounts(prev => {
      const next = prev.map(a => {
        if (a.id === fromAccountId) return { ...a, balance: a.balance - amount };
        if (a.id === toAccountId) return { ...a, balance: a.balance + amount };
        return a;
      });
      StorageService.setItem('mylife_accounts', next);
      return next;
    });
    triggerConfetti();
    return true;
  };

  // --- Categories ---
  const addCategory = (cat: Omit<Category, 'id'>) => {
    const newCat: Category = {
      ...cat,
      id: 'cat_' + Date.now(),
    };
    setCategories(prev => [...prev, newCat]);
  };

  const deleteCategory = (id: string) => {
    setCategories(prev => prev.filter(c => c.id !== id));
  };

  // --- Accounts ---
  const addAccount = (acc: Omit<Account, 'id'>) => {
    const newAcc: Account = {
      ...acc,
      id: 'acc_' + Date.now(),
      folderId: acc.folderId || (selectedFinanceFolderId !== 'all' ? selectedFinanceFolderId : 'f_personal'),
    };
    setAccounts(prev => {
      const next = [...prev, newAcc];
      StorageService.setItem('mylife_accounts', next);
      return next;
    });
  };

  const editAccount = (id: string, updated: Partial<Account>) => {
    setAccounts(prev => {
      const next = prev.map(a => (a.id === id ? { ...a, ...updated } : a));
      StorageService.setItem('mylife_accounts', next);
      return next;
    });
  };

  const updateAccountBalance = (id: string, balance: number) => {
    setAccounts(prev => {
      const next = prev.map(a => (a.id === id ? { ...a, balance } : a));
      StorageService.setItem('mylife_accounts', next);
      return next;
    });
  };

  const deleteAccount = (id: string) => {
    setAccounts(prev => {
      const next = prev.filter(a => a.id !== id);
      StorageService.setItem('mylife_accounts', next);
      return next;
    });
  };

  // --- Budgets ---
  const addBudget = (budget: Omit<Budget, 'id'>) => {
    const cat = categories.find(c => c.id === budget.categoryId);
    const newBudget: Budget = {
      ...budget,
      id: 'b_' + Date.now(),
      categoryName: cat?.name || '',
      folderId: budget.folderId || (selectedFinanceFolderId !== 'all' ? selectedFinanceFolderId : 'f_personal'),
    };
    setBudgets(prev => {
      const existing = prev.filter(b => !(b.categoryId === budget.categoryId && b.month === budget.month));
      return [...existing, newBudget];
    });
  };

  const editBudget = (id: string, updated: Partial<Budget>) => {
    setBudgets(prev =>
      prev.map(b => (b.id === id ? { ...b, ...updated } : b))
    );
  };

  const deleteBudget = (id: string) => {
    setBudgets(prev => prev.filter(b => b.id !== id));
  };

  // --- Debts ---
  const addDebt = (debt: Omit<Debt, 'id' | 'createdAt' | 'status'>) => {
    const newDebt: Debt = {
      ...debt,
      id: 'debt_' + Date.now(),
      status: 'pending',
      folderId: debt.folderId || (selectedFinanceFolderId !== 'all' ? selectedFinanceFolderId : 'f_personal'),
      createdAt: getLocalDateString(),
    };
    setDebts(prev => [newDebt, ...prev]);
  };

  const editDebt = (id: string, updated: Partial<Debt>) => {
    setDebts(prev =>
      prev.map(d => (d.id === id ? { ...d, ...updated } : d))
    );
  };

  const settleDebt = (id: string) => {
    setDebts(prev =>
      prev.map(d => (d.id === id ? { ...d, status: 'settled' } : d))
    );
    triggerConfetti();
  };

  const deleteDebt = (id: string) => {
    setDebts(prev => prev.filter(d => d.id !== id));
  };

  // --- Savings Goals ---
  const addSavingsGoal = (goal: Omit<SavingsGoal, 'id'>) => {
    const newGoal: SavingsGoal = {
      ...goal,
      id: 'sg_' + Date.now(),
      folderId: goal.folderId || (selectedFinanceFolderId !== 'all' ? selectedFinanceFolderId : 'f_personal'),
    };
    setSavingsGoals(prev => [...prev, newGoal]);
    triggerConfetti();
  };

  const editSavingsGoal = (id: string, updated: Partial<SavingsGoal>) => {
    setSavingsGoals(prev =>
      prev.map(g => (g.id === id ? { ...g, ...updated } : g))
    );
  };

  const updateSavingsGoalProgress = (id: string, deltaAmount: number) => {
    setSavingsGoals(prev =>
      prev.map(g => (g.id === id ? { ...g, currentAmount: Math.max(0, g.currentAmount + deltaAmount) } : g))
    );
  };

  const deleteSavingsGoal = (id: string) => {
    setSavingsGoals(prev => prev.filter(g => g.id !== id));
  };

  // --- Recurring Bills ---
  const addRecurringBill = (bill: Omit<RecurringBill, 'id'>) => {
    const cat = categories.find(c => c.id === bill.categoryId);
    const newBill: RecurringBill = {
      ...bill,
      id: 'rb_' + Date.now(),
      categoryName: cat?.name || bill.categoryName,
      folderId: bill.folderId || (selectedFinanceFolderId !== 'all' ? selectedFinanceFolderId : 'f_personal'),
    };
    setRecurringBills(prev => [...prev, newBill]);
  };

  const editRecurringBill = (id: string, updated: Partial<RecurringBill>) => {
    setRecurringBills(prev =>
      prev.map(r => (r.id === id ? { ...r, ...updated } : r))
    );
  };

  const deleteRecurringBill = (id: string) => {
    setRecurringBills(prev => prev.filter(b => b.id !== id));
  };

  const payRecurringBill = (id: string, accountId: string) => {
    const bill = recurringBills.find(b => b.id === id);
    if (!bill) return;

    addTransaction({
      type: bill.type,
      amount: bill.amount,
      categoryId: bill.categoryId,
      accountId: accountId,
      date: getLocalDateString(),
      note: `Paid recurring bill: ${bill.title}`,
      paymentMethod: 'auto',
    });
    triggerConfetti();
  };

  // --- Notes ---
  const addNote = (note: Omit<Note, 'id' | 'createdAt' | 'updatedAt'>) => {
    const now = new Date().toISOString();
    const newNote: Note = {
      ...note,
      id: 'note_' + Date.now(),
      createdAt: now,
      updatedAt: now,
    };
    setNotes(prev => [newNote, ...prev]);
  };

  const updateNote = (id: string, updated: Partial<Note>) => {
    const now = new Date().toISOString();
    setNotes(prev =>
      prev.map(n => (n.id === id ? { ...n, ...updated, updatedAt: now } : n))
    );
  };

  const deleteNote = (id: string) => {
    const now = new Date().toISOString();
    setNotes(prev =>
      prev.map(n => {
        if (n.id === id) {
          return { ...n, isTrashed: true, deletedAt: now, pinned: false };
        }
        return n;
      })
    );
  };

  const restoreNote = (id: string) => {
    const now = new Date().toISOString();
    setNotes(prev =>
      prev.map(n => (n.id === id ? { ...n, isTrashed: false, deletedAt: undefined, updatedAt: now } : n))
    );
  };

  const permanentlyDeleteNote = (id: string) => {
    setNotes(prev => prev.filter(n => n.id !== id));
  };

  const emptyTrash = () => {
    setNotes(prev => prev.filter(n => !n.isTrashed));
  };

  const togglePinNote = (id: string) => {
    setNotes(prev =>
      prev.map(n => (n.id === id ? { ...n, pinned: !n.pinned } : n))
    );
  };

  const duplicateNote = (id: string) => {
    const original = notes.find(n => n.id === id);
    if (!original) return;
    const now = new Date().toISOString();
    const cloned: Note = {
      ...original,
      id: 'note_' + Date.now(),
      title: `${original.title} (Copy)`,
      pinned: false,
      createdAt: now,
      updatedAt: now,
    };
    setNotes(prev => [cloned, ...prev]);
  };

  const toggleNoteChecklist = (noteId: string, itemId: string) => {
    const now = new Date().toISOString();
    setNotes(prev =>
      prev.map(n => {
        if (n.id !== noteId || !n.checklist) return n;
        const updatedChecklist = n.checklist.map(item =>
          item.id === itemId ? { ...item, completed: !item.completed } : item
        );
        return {
          ...n,
          checklist: updatedChecklist,
          updatedAt: now,
        };
      })
    );
  };

  // --- Tasks ---
  const addTask = (task: Omit<Task, 'id' | 'createdAt' | 'status'>) => {
    const now = new Date().toISOString();
    const newTask: Task = {
      ...task,
      id: 'task_' + Date.now(),
      status: 'pending',
      createdAt: getLocalDateString(),
      updatedAt: now,
    };
    setTasks(prev => [newTask, ...prev]);
  };

  const toggleTask = (id: string) => {
    const now = new Date().toISOString();
    setTasks(prev =>
      prev.map(t => {
        if (t.id === id) {
          const nextStatus = t.status === 'completed' ? 'pending' : 'completed';
          if (nextStatus === 'completed') {
            triggerConfetti();
          }
          return {
            ...t,
            status: nextStatus,
            completedAt: nextStatus === 'completed' ? now : undefined,
            updatedAt: now,
          };
        }
        return t;
      })
    );
  };

  const editTask = (id: string, updated: Partial<Task>) => {
    const now = new Date().toISOString();
    setTasks(prev =>
      prev.map(t => (t.id === id ? { ...t, ...updated, updatedAt: now } : t))
    );
  };

  const deleteTask = (id: string) => {
    setTasks(prev => prev.filter(t => t.id !== id));
  };

  // --- Habits ---
  const addHabit = (habit: Omit<Habit, 'id' | 'createdAt'>) => {
    const now = new Date().toISOString();
    const newHabit: Habit = {
      ...habit,
      id: 'h_' + Date.now(),
      createdAt: getLocalDateString(),
      updatedAt: now,
    };
    setHabits(prev => [...prev, newHabit]);
  };

  const toggleHabit = (habitId: string, date: string = getLocalDateString()) => {
    const now = new Date().toISOString();
    setHabitLogs(prev => {
      const existingIdx = prev.findIndex(hl => hl.habitId === habitId && hl.date === date);
      if (existingIdx >= 0) {
        const nextLogs = [...prev];
        const currentCompleted = nextLogs[existingIdx].completed;
        if (currentCompleted) {
          nextLogs.splice(existingIdx, 1);
        } else {
          nextLogs[existingIdx] = {
            ...nextLogs[existingIdx],
            completed: true,
            updatedAt: now,
          };
          triggerConfetti();
        }
        return nextLogs;
      } else {
        triggerConfetti();
        return [...prev, { id: 'hl_' + Date.now(), habitId, date, completed: true, updatedAt: now }];
      }
    });
  };

  const editHabit = (id: string, updated: Partial<Habit>) => {
    const now = new Date().toISOString();
    setHabits(prev =>
      prev.map(h => (h.id === id ? { ...h, ...updated, updatedAt: now } : h))
    );
  };

  const deleteHabit = (id: string) => {
    setHabits(prev => prev.filter(h => h.id !== id));
    setHabitLogs(prev => prev.filter(hl => hl.habitId !== id));
  };

  // --- Yearly Goals & Learning Roadmap ---
  const addYearlyGoal = (goal: Omit<YearlyGoal, 'id' | 'createdAt'>) => {
    const newGoal: YearlyGoal = {
      ...goal,
      id: 'yg_' + Date.now(),
      createdAt: getLocalDateString(),
      progress: goal.progress || 0,
      milestones: goal.milestones || [],
    };
    setYearlyGoals(prev => [newGoal, ...prev]);
    triggerConfetti();
  };

  const editYearlyGoal = (id: string, updated: Partial<YearlyGoal>) => {
    setYearlyGoals(prev =>
      prev.map(g => (g.id === id ? { ...g, ...updated } : g))
    );
  };

  const deleteYearlyGoal = (id: string) => {
    setYearlyGoals(prev => prev.filter(g => g.id !== id));
  };

  const toggleGoalMilestone = (goalId: string, milestoneId: string) => {
    setYearlyGoals(prev =>
      prev.map(g => {
        if (g.id !== goalId) return g;
        const updatedMilestones = g.milestones.map(m =>
          m.id === milestoneId ? { ...m, completed: !m.completed } : m
        );
        const total = updatedMilestones.length;
        const completedCount = updatedMilestones.filter(m => m.completed).length;
        const autoProgress = total > 0 ? Math.round((completedCount / total) * 100) : g.progress;
        const autoStatus: GoalStatus = autoProgress === 100 ? 'completed' : autoProgress > 0 ? 'in_progress' : g.status;
        if (autoProgress === 100) triggerConfetti();
        return {
          ...g,
          milestones: updatedMilestones,
          progress: autoProgress,
          status: autoStatus,
          completedAt: autoProgress === 100 ? new Date().toISOString() : undefined,
        };
      })
    );
  };

  const updateGoalProgress = (goalId: string, progress: number) => {
    const clamped = Math.max(0, Math.min(100, progress));
    if (clamped === 100) triggerConfetti();
    setYearlyGoals(prev =>
      prev.map(g => {
        if (g.id !== goalId) return g;
        return {
          ...g,
          progress: clamped,
          status: clamped === 100 ? 'completed' : clamped > 0 ? 'in_progress' : g.status,
          completedAt: clamped === 100 ? new Date().toISOString() : undefined,
        };
      })
    );
  };

  // --- Resources & Links Actions ---
  const addResource = (res: Omit<ResourceLink, 'id' | 'createdAt'>) => {
    const newResource: ResourceLink = {
      ...res,
      id: 'res_' + Date.now(),
      createdAt: new Date().toISOString(),
      clickCount: res.clickCount || 0,
      tags: res.tags || [],
    };
    setResources(prev => [newResource, ...prev]);
    triggerConfetti();
  };

  const editResource = (id: string, updated: Partial<ResourceLink>) => {
    const now = new Date().toISOString();
    setResources(prev =>
      prev.map(r => (r.id === id ? { ...r, ...updated, updatedAt: now } : r))
    );
  };

  const deleteResource = (id: string) => {
    setResources(prev => prev.filter(r => r.id !== id));
  };

  const toggleFavoriteResource = (id: string) => {
    setResources(prev =>
      prev.map(r => (r.id === id ? { ...r, isFavorite: !r.isFavorite } : r))
    );
  };

  const togglePinResource = (id: string) => {
    setResources(prev =>
      prev.map(r => (r.id === id ? { ...r, isPinned: !r.isPinned } : r))
    );
  };

  const recordResourceVisit = (id: string) => {
    setResources(prev =>
      prev.map(r =>
        r.id === id
          ? { ...r, clickCount: (r.clickCount || 0) + 1, lastVisited: new Date().toISOString() }
          : r
      )
    );
  };

  const importResources = (imported: ResourceLink[]) => {
    if (!imported || imported.length === 0) return;
    setResources(prev => {
      const existingUrls = new Set(prev.map(r => r.url.toLowerCase().trim()));
      const filtered = imported.filter(
        item => item.url && !existingUrls.has(item.url.toLowerCase().trim())
      );
      return [...filtered, ...prev];
    });
    triggerConfetti();
  };

  // --- Personal Report Actions ---
  const updateDailyReport = (entry: DailyReportEntry) => {
    setPersonalReports(prev => {
      const idx = prev.findIndex(r => r.date === entry.date);
      if (idx >= 0) {
        const copy = [...prev];
        copy[idx] = { ...copy[idx], ...entry };
        return copy;
      }
      return [...prev, entry].sort((a, b) => a.date.localeCompare(b.date));
    });
  };

  const bulkUpdateDailyReports = (entries: DailyReportEntry[]) => {
    setPersonalReports(prev => {
      const map = new Map<string, DailyReportEntry>();
      prev.forEach(r => map.set(r.date, r));
      entries.forEach(e => map.set(e.date, { ...map.get(e.date), ...e }));
      return Array.from(map.values()).sort((a, b) => a.date.localeCompare(b.date));
    });
  };

  const clearMonthlyReports = (monthStr: string) => {
    setPersonalReports(prev => prev.filter(r => !r.date.startsWith(monthStr)));
  };

  // --- Backup & Reset ---
  const resetData = () => {
    StorageService.clearAll();
    const fresh = StorageService.loadAll();
    setAccounts(fresh.accounts);
    setCategories(fresh.categories);
    setTransactions(fresh.transactions);
    setBudgets(fresh.budgets);
    setDebts(fresh.debts);
    setNotes(fresh.notes);
    setTasks(fresh.tasks);
    setHabits(fresh.habits);
    setHabitLogs(fresh.habitLogs);
    setPersonalReports(fresh.personalReports);
    setYearlyGoals(fresh.yearlyGoals);
    setResources(fresh.resources);
    CloudSync.pushToCloud();
  };

  const importBackup = (jsonStr: string): boolean => {
    const success = StorageService.importBackupJSON(jsonStr);
    if (success) {
      const fresh = StorageService.loadAll();
      setAccounts(fresh.accounts);
      setCategories(fresh.categories);
      setTransactions(fresh.transactions);
      setBudgets(fresh.budgets);
      setDebts(fresh.debts);
      setNotes(fresh.notes);
      setTasks(fresh.tasks);
      setHabits(fresh.habits);
      setHabitLogs(fresh.habitLogs);
      setPersonalReports(fresh.personalReports);
      setYearlyGoals(fresh.yearlyGoals);
      setResources(fresh.resources);
      triggerConfetti();
    }
    return success;
  };

  return (
    <AppContext.Provider
      value={{
        user,
        isAuthenticated,
        currentPage,
        language,
        theme,
        currency,
        t,
        financeFolders,
        selectedFinanceFolderId,
        setSelectedFinanceFolderId,
        accounts,
        categories,
        transactions,
        budgets,
        debts,
        savingsGoals,
        recurringBills,
        notes,
        tasks,
        habits,
        habitLogs,
        personalReports,
        setCurrentPage,
        isSearchOpen,
        setIsSearchOpen,
        activeModal,
        setActiveModal,
        setLanguage,
        setTheme,
        setCurrency,
        login,
        signup,
        logout,
        updateUser,
        triggerConfetti,
        addFinanceFolder,
        updateFinanceFolder,
        deleteFinanceFolder,
        addTransaction,
        editTransaction,
        deleteTransaction,
        transferMoney,
        addCategory,
        deleteCategory,
        addAccount,
        editAccount,
        updateAccountBalance,
        deleteAccount,
        addBudget,
        editBudget,
        deleteBudget,
        addDebt,
        editDebt,
        settleDebt,
        deleteDebt,
        addSavingsGoal,
        editSavingsGoal,
        updateSavingsGoalProgress,
        deleteSavingsGoal,
        addRecurringBill,
        editRecurringBill,
        deleteRecurringBill,
        payRecurringBill,
        addNote,
        updateNote,
        deleteNote,
        restoreNote,
        permanentlyDeleteNote,
        emptyTrash,
        togglePinNote,
        duplicateNote,
        toggleNoteChecklist,
        addTask,
        toggleTask,
        editTask,
        deleteTask,
        addHabit,
        editHabit,
        toggleHabit,
        deleteHabit,
        yearlyGoals,
        addYearlyGoal,
        editYearlyGoal,
        deleteYearlyGoal,
        toggleGoalMilestone,
        updateGoalProgress,
        resources,
        addResource,
        editResource,
        deleteResource,
        toggleFavoriteResource,
        togglePinResource,
        recordResourceVisit,
        importResources,
        updateDailyReport,
        bulkUpdateDailyReports,
        clearMonthlyReports,
        resetData,
        importBackup,
        changePassword,
        // App Lock & Biometrics Security
        isAppLockEnabled,
        isBiometricsEnabled,
        isBiometricsAvailable: isBiometricsAvailableState,
        isAppLocked,
        autoLockTimeout,
        lockApp,
        unlockWithPin,
        unlockWithBiometrics,
        enableAppLock,
        disableAppLock,
        changeAppLockPin,
        toggleBiometrics,
        setAutoLockTimeout,
        // Real-Time Cloud Sync
        syncState,
        syncNow: () => CloudSync.pushToCloud(),
        pullNow: (force?: boolean) => CloudSync.pullFromCloud(force),
        setSyncKey: (k: string) => CloudSync.setSyncKey(k),
        setSyncEndpoint: (ep: string) => CloudSync.setEndpoint(ep),
        setAutoSync: (en: boolean) => CloudSync.setAutoSync(en),
      }}
    >
      {children}
    </AppContext.Provider>
  );
};

export const useApp = () => {
  const context = useContext(AppContext);
  if (!context) {
    throw new Error('useApp must be used within an AppProvider');
  }
  return context;
};
